<template>
    <div class="transaction-activity-section">
        <div class="section-divider">
            <span class="section-title">Transaction Activity</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top Programs by Transaction Count</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="txRankChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Transaction Market Share</div>
                    <div class="panel-subtitle">% of network transactions by program</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="txShareChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Transaction Volume Over Time</div>
                    <div class="panel-subtitle">Top programs + Others</div>
                </div>
                <div class="panel-body chart-extra-tall">
                    <canvas ref="txTimeChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';
import { formatCompact, formatPubkey, programLabel } from '../../composables/useFormatters';
import { colors, getBarChartOptions, getStackedBarChartOptions } from '../../composables/useChartConfig';
import { GRANULARITY_MODES, transformValue, getUnitSuffix } from '../../composables/useGranularityToggle';

// Register treemap components
Chart.register(TreemapController, TreemapElement);

export default {
    name: 'ProgramTransactionActivity',

    props: {
        programs: {
            type: Array,
            required: true
        },
        interval: {
            type: String,
            default: '1h'
        },
        granularityMode: {
            type: String,
            default: GRANULARITY_MODES.TOTAL
        }
    },

    emits: ['zoom'],

    data() {
        return {
            charts: {},
            colors: colors
        };
    },

    watch: {
        programs: {
            handler() {
                if (Object.keys(this.charts).length > 0) {
                    this.updateCharts();
                }
            },
            deep: true
        },
        granularityMode() {
            if (Object.keys(this.charts).length > 0) {
                this.updateCharts();
            }
        }
    },

    mounted() {
        this.initCharts();
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
    },

    methods: {
        formatCompact,
        formatPubkey,
        programLabel,

        transformForGranularity(value) {
            return transformValue(value, this.granularityMode, this.interval);
        },

        getGranularityLabel(baseLabel) {
            const suffix = getUnitSuffix(this.granularityMode, this.interval, '');
            return suffix ? `${baseLabel} ${suffix}` : baseLabel;
        },

        initCharts() {
            // Transaction Rank Chart
            this.charts.txRank = markRaw(new Chart(this.$refs.txRankChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({
                    horizontal: true,
                    showLegend: false,
                    formatCallback: v => formatCompact(v)
                })
            }));

            // Transaction Share Chart (Treemap)
            this.charts.txShare = markRaw(new Chart(this.$refs.txShareChart, {
                type: 'treemap',
                data: {
                    datasets: [{
                        tree: [],
                        key: 'value',
                        groups: ['label'],
                        spacing: 1,
                        borderWidth: 1,
                        borderColor: 'rgba(255,255,255,0.2)',
                        backgroundColor: (ctx) => {
                            if (!ctx.raw) return this.colors.series[0];
                            const item = ctx.raw;
                            // Color based on fee/tx ratio (higher = warmer colors)
                            const ratio = item._data?.ratio || 0;
                            const maxRatio = 0.001; // Expected max fee/tx ratio
                            const normalizedRatio = Math.min(ratio / maxRatio, 1);
                            // Gradient from purple (low) to orange (high)
                            const hue = 280 - (normalizedRatio * 60); // 280 (purple) to 220 (blue) to 30 (orange)
                            return `hsla(${hue}, 70%, 50%, 0.8)`;
                        },
                        labels: {
                            display: true,
                            formatter: (ctx) => ctx.raw?._data?.label || ''
                        }
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                title: (items) => items[0]?.raw?._data?.label || '',
                                label: (item) => {
                                    const data = item.raw?._data;
                                    if (!data) return '';
                                    return [
                                        `Transactions: ${formatCompact(data.value)}`,
                                        `Fee/Tx: ${(data.ratio * 1e9).toFixed(2)} lamports`
                                    ];
                                }
                            }
                        }
                    }
                }
            }));

            // Transaction Time Chart (Stacked)
            this.charts.txTime = markRaw(new Chart(this.$refs.txTimeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({
                    formatCallback: v => formatCompact(v),
                    onZoom: (context) => this.handleZoom(context)
                })
            }));

            this.updateCharts();
        },

        updateCharts() {
            if (!this.programs.length) return;

            const top10 = this.programs.slice(0, 10);
            const topN = this.programs.slice(0, 25); // Top 25 for time series
            const timeLabels = topN[0]?.timeSeries?.map(t => t.label) || [];
            const transform = this.transformForGranularity.bind(this);

            // Transaction Rank - add borderWidth: 0 to fix gray borders
            this.charts.txRank.data = {
                labels: top10.map(p => programLabel(p)),
                datasets: [{
                    label: this.getGranularityLabel('Transactions'),
                    data: top10.map(p => transform(p.metrics.totalTransactions)),
                    backgroundColor: this.colors.series,
                    borderWidth: 0
                }]
            };
            this.charts.txRank.update();

            // Transaction Share (Treemap) - shows share %, not transformed
            const treemapData = top10.map(p => ({
                label: programLabel(p),
                value: p.metrics.totalTransactions,
                ratio: p.metrics.totalTransactions > 0
                    ? p.metrics.totalFees / p.metrics.totalTransactions
                    : 0
            }));
            this.charts.txShare.data.datasets[0].tree = treemapData;
            this.charts.txShare.update();

            // Transaction Time (stacked) - 25 programs + Others
            // Calculate "Others" as remaining programs not in top 25
            const othersData = timeLabels.map((_, timeIdx) => {
                const topNTotal = topN.reduce((sum, p) => {
                    return sum + (p.timeSeries?.[timeIdx]?.txCount || 0);
                }, 0);
                // Get total from all programs
                const allTotal = this.programs.reduce((sum, p) => {
                    return sum + (p.timeSeries?.[timeIdx]?.txCount || 0);
                }, 0);
                return transform(Math.max(0, allTotal - topNTotal));
            });

            const datasets = topN.map((p, i) => ({
                label: programLabel(p),
                data: p.timeSeries?.map(t => transform(t.txCount)) || [],
                backgroundColor: this.colors.series[i % this.colors.series.length],
                borderWidth: 0
            }));

            // Add "Others" category
            if (othersData.some(v => v > 0)) {
                datasets.push({
                    label: 'Others',
                    data: othersData,
                    backgroundColor: '#4b5563', // Gray for others
                    borderWidth: 0
                });
            }

            this.charts.txTime.data = {
                labels: timeLabels,
                datasets: datasets
            };
            this.charts.txTime.update();
        },

        handleZoom(context) {
            // Get the visible range from the chart's x-axis
            const xScale = context.chart.scales.x;
            const minIndex = Math.floor(xScale.min);
            const maxIndex = Math.ceil(xScale.max);

            // Get timestamps from the programs data
            const timeSeries = this.programs[0]?.timeSeries || [];
            if (timeSeries.length > 0) {
                const startTs = timeSeries[Math.max(0, minIndex)]?.timestamp;
                const endTs = timeSeries[Math.min(timeSeries.length - 1, maxIndex)]?.timestamp;

                if (startTs && endTs) {
                    this.$emit('zoom', {
                        start: new Date(startTs),
                        end: new Date(endTs),
                        source: 'transaction-volume'
                    });
                }
            }
        }
    }
};
</script>

<style scoped>
.chart-tall {
    min-height: 280px;
}
</style>
